import React, { useContext, useEffect, useState } from "react";
import { UncontrolledTooltip } from "reactstrap";
import { injectIntl } from "react-intl";
import { lightOrDark } from "../../../Base/Utilities/LightOrDark";
import { getDistanceBetweenTwoColors } from "../../../Base/Utilities/Color";
import { FixtureDetailsContext } from "../../../../Config/FixtureDetails";
import { getIsPlayerDisqualifiedBecauseOfFoulTypes, getIsFouledOut } from "../../../Base/Utilities/Player";
import PlayerFaultCounter from "../components/PlayerFaultCounter";

const redColor = "rgb(255, 0, 0)";

// we use yellow color instead of red, if the team color is too close to red
function getFoulColor(teamColor) {
  const distance = getDistanceBetweenTwoColors(redColor, teamColor);
  const isDistanceAcceptable = distance > 350;

  return isDistanceAcceptable ? "red" : "yellow";
}

const Player = (props) => {
  const { player, entityId, teamColor, currentState, eventStore, intl, isPreSubbed, forcedActiveState, subMode } =
    props;
  const { formatMessage } = intl;
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const {
    fixtureProfile: { maxFoulsPersonal },
  } = useContext(FixtureDetailsContext);

  const getIsPlayerActive = () => {
    if (forcedActiveState !== undefined) {
      return forcedActiveState;
    }

    return player.desiredActiveState !== undefined ? player.desiredActiveState : player.active;
  };

  const isPlayerActive = getIsPlayerActive();

  const foulCount = currentState?.statistics?.[entityId]?.persons?.[player.personId]?.foulsTotal ?? 0;

  const isPlayerDisqualifiedBecauseOfFoulTypes = getIsPlayerDisqualifiedBecauseOfFoulTypes(player.personId, eventStore);

  const isPlayerDisqualified = isPlayerDisqualifiedBecauseOfFoulTypes || foulCount >= maxFoulsPersonal;

  const isFouledOut = getIsFouledOut(player.personId, eventStore, maxFoulsPersonal);

  const foulColor = getFoulColor(teamColor);

  useEffect(() => () => setTooltipOpen(false), []);

  const getStyleObject = () => {
    const foulNearMax = foulCount + 1 === maxFoulsPersonal;

    if (subMode === "top") {
      if (foulNearMax) {
        return {
          backgroundColor: teamColor,
          border: `2px solid ${foulColor}`,
        };
      }
      if (isPlayerDisqualified) {
        return {
          backgroundColor: "#000000cc",
          border: "1px solid #000000cc",
          color: "#5a5a5a",
        };
      }
      if (player.active && !forcedActiveState) {
        return {
          backgroundColor: "#242b34",
          color: "white",
        };
      }
      return {
        backgroundColor: teamColor,
        border: `1px solid ${lightOrDark(teamColor) === "light" ? "#202020" : teamColor}`,
      };
    } else if (subMode === "bottom") {
      if (player.active) {
        return {
          backgroundColor: "#9ea0a4",
          color: `#d2d2d2`,
        };
      } else if (forcedActiveState) {
        return {
          backgroundColor: teamColor,
          border: `1px solid ${lightOrDark(teamColor) === "light" ? "#202020" : teamColor}`,
        };
      }
      if (isPreSubbed) {
        return {
          backgroundColor: "#242b34",
          color: "white",
          border: `3px solid ${teamColor}`,
        };
      }
      return {
        backgroundColor: "#242b34",
        color: "white",
      };
    }

    if (isPlayerDisqualified) {
      return {
        backgroundColor: teamColor,
        border: "1px solid transparent",
      };
    }

    if (!isPlayerActive && isPreSubbed) {
      return {
        backgroundColor: teamColor,
        border: `3px solid ${teamColor}`,
      };
    }

    if (foulNearMax) {
      return {
        backgroundColor: teamColor,
        border: `2px solid ${foulColor}`,
      };
    }

    if (isPlayerActive) {
      return {
        backgroundColor: teamColor,
        border: `1px solid ${lightOrDark(teamColor) === "light" ? "#202020" : teamColor}`,
      };
    } else {
      return {
        backgroundColor: teamColor,
        borderColor: teamColor,
      };
    }
  };

  const handleClick = (e) => {
    if (props.onClick) {
      props.onClick(e);
    }
  };

  const getFoulTitle = () => {
    const message = {
      id: "preSubbed",
      defaultMessage: "SUB",
    };

    if (isFouledOut) {
      message.id = "fouledOut";
      message.defaultMessage = "Fouled Out";
    } else if (isPlayerDisqualified) {
      message.id = "disqualified";
      message.defaultMessage = "Disqualified";
    }

    return formatMessage(message);
  };

  return (
    <div
      id={`player-tile-${player.personId}`}
      className={`player ${subMode ? "sub-view" : `active-${isPlayerActive}`} ${lightOrDark(teamColor)}`}
      key={player.personId}
      style={getStyleObject()}
      onClick={handleClick}
      onMouseEnter={() => {
        setTooltipOpen(true);
      }}
      onMouseLeave={() => {
        setTooltipOpen(false);
      }}
    >
      <span>{player.bib}</span>
      <PlayerFaultCounter
        maxFoulsPersonal={maxFoulsPersonal}
        foulCount={foulCount}
        personId={player.personId}
        teamColor={teamColor}
        isPlayerDisqualified={isPlayerDisqualified}
        forceLightColor={(!isPlayerActive || subMode === "bottom") && (!forcedActiveState || player.active)}
        subMode={subMode}
        subbedIn={player.active}
      />
      {isTooltipOpen && (isFouledOut || isPlayerDisqualified || isPreSubbed) && (
        <UncontrolledTooltip target={`player-tile-${player.personId}`} style={{ border: `1px solid ${foulColor}` }}>
          {getFoulTitle()}
        </UncontrolledTooltip>
      )}
    </div>
  );
};

export default injectIntl(Player);
